import { useState, useEffect } from "react";
import { trpc } from "../utils/trpc";
import TagSelector from "./TagSelector";
import "../styles/TagManagerModal.css";

interface Tag {
  id: number;
  name: string;
  category_id?: number;
}

type Mode = "select" | "edit";

interface TagManagerModalProps {
  isOpen: boolean;
  onClose: () => void;

  mode: Mode;

  initialTags: Tag[];
  originalTags?: Tag[];

  min?: number;
  max?: number;

  // Preferred: inject save logic
  onSave?: (tags: Tag[]) => Promise<void> | void;

  // Optional fallback (legacy support)
  profileType?: "member" | "venue_owner" | "group" | "event";
  venueId?: string;
  groupId?: string;
  eventId?: string;
}

function TagManagerModal({
  isOpen,
  onClose,
  mode,
  initialTags,
  originalTags,
  min = 0,
  max = Infinity,
  onSave,
  profileType,
  venueId,
  groupId,
  eventId,
}: TagManagerModalProps) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [error, setError] = useState<string | null>(null);

  const utils = trpc.useUtils();

  // Mutations (only used if onSave is NOT provided)
  const addTagsToMe = trpc.tags.addTagsToMe.useMutation();
  const removeTagsFromMe = trpc.tags.removeTagsFromMe.useMutation();
  const addTagToVenue = trpc.tags.addTagToVenue.useMutation();
  const removeTagFromVenue = trpc.tags.removeTagFromVenue.useMutation();
  const addTagToGroup = trpc.tags.addTagToGroup.useMutation();
  const removeTagFromGroup = trpc.tags.removeTagFromGroup.useMutation();
  const addTagToEvent = trpc.tags.addTagToEvent.useMutation();
  const removeTagFromEvent = trpc.tags.removeTagFromEvent.useMutation();

  // Initialize state ONLY when opening
  useEffect(() => {
    if (isOpen) {
      setTags(initialTags);
      setError(null);
    }
  }, [isOpen, initialTags]);

  const handleInternalSave = async () => {
    if (!originalTags || !profileType) return;

    const tagsToAdd = tags.filter(
      (t) => !originalTags.some((o) => o.id === t.id)
    );

    const tagsToRemove = originalTags.filter(
      (o) => !tags.some((t) => t.id === o.id)
    );

    if (profileType === "member") {
      if (tagsToRemove.length) {
        await removeTagsFromMe.mutateAsync({
          tagIds: tagsToRemove.map((t) => t.id),
        });
      }
      if (tagsToAdd.length) {
        await addTagsToMe.mutateAsync({
          tagIds: tagsToAdd.map((t) => t.id),
        });
      }
      await utils.tags.getUserTags.invalidate();
    }

    if (profileType === "venue_owner") {
      if (!venueId) throw new Error("Venue ID required");

      if (tagsToRemove.length) {
        await removeTagFromVenue.mutateAsync({
          venueId,
          tagIds: tagsToRemove.map((t) => t.id),
        });
      }

      if (tagsToAdd.length) {
        await Promise.all(
          tagsToAdd.map((t) =>
            addTagToVenue.mutateAsync({ venueId, tagId: t.id })
          )
        );
      }

      await utils.tags.getVenueTags.invalidate({ venueId });
    }

    if (profileType === "group") {
      if (!groupId) throw new Error("Group ID required");

      if (tags.length === 0) {
        setError("Groups must have at least one interest.");
        return;
      }

      if (tagsToRemove.length) {
        await removeTagFromGroup.mutateAsync({
          groupId,
          tagIds: tagsToRemove.map((t) => t.id),
        });
      }

      if (tagsToAdd.length) {
        await Promise.all(
          tagsToAdd.map((t) =>
            addTagToGroup.mutateAsync({ groupId, tagId: t.id })
          )
        );
      }

      await utils.tags.getGroupTags.invalidate({ groupId });
    }

    if (profileType === "event") {
      if (!eventId) throw new Error("Event ID required");

      if (tags.length === 0) {
        setError("Events must have at least one interest.");
        return;
      }

      if (tagsToRemove.length) {
        await removeTagFromEvent.mutateAsync({
          eventId,
          tagIds: tagsToRemove.map((t) => t.id),
        });
      }

      if (tagsToAdd.length) {
        await Promise.all(
          tagsToAdd.map((t) =>
            addTagToEvent.mutateAsync({ eventId, tagId: t.id })
          )
        );
      }

      await utils.tags.getEventTags.invalidate({ eventId });
    }
  };

  const handleSave = async () => {
    if (tags.length < min) {
      setError(`Please select at least ${min} interest(s).`);
      return;
    }

    // Preferred path
    if (onSave) {
      await onSave(tags);
      onClose();
      return;
    }

    // Fallback internal logic
    if (mode === "edit") {
      await handleInternalSave();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content tag-edit-modal">
        {/* Header */}
        <div className="modal-header">
          <h2>{mode === "edit" ? "Edit Interests" : "Select Interests"}</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Shared ui  */}
        <TagSelector
          value={tags}
          onChange={setTags}
          min={min}
          max={max}
        />

        {error && <p className="tag-error">{error}</p>}

        {/* Footer */}
        <div className="modal-actions">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={tags.length < min}
          >
            {mode === "edit" ? "Save Changes" : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TagManagerModal;